import React from "react";
import { useFragment } from "react-relay";

import { faSync } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { graphql } from "relay-runtime";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button } from "~/ui";
import { Modal, TextField } from "~/ui/legacyFellowIndex";
import type { ModalProps } from "~/ui/modals";

import { HighlightFeedbackSource } from "./utils";

import type {
	DislikeModal_meetingRecording$key,
	DislikeModal_meetingRecording$data as DislikeModalMeetingRecording,
} from "~/__generated__/DislikeModal_meetingRecording.graphql";
import type { DislikeModalFeedbackSummaryMutation } from "~/__generated__/DislikeModalFeedbackSummaryMutation.graphql";

import styles from "./css/DislikeModal.module.css";

type FeedbackOption = Exclude<
	keyof NonNullable<NonNullable<DislikeModalMeetingRecording["activeSummary"]>["feedback"]>,
	"id" | "otherFeedback"
>;

const feedbackOptionsText: { [key in FeedbackOption]: string } = {
	missingInformation: "Missing information",
	notFactuallyCorrect: "Not factually correct",
	dontLikeTheTone: "Don't like the tone",
	other: "Other",
};

const getInitialFeedbackState = (
	previousFeedback?: NonNullable<DislikeModalMeetingRecording["activeSummary"]>["feedback"],
): { [key in FeedbackOption]: boolean } => {
	const initialFeedback: { [key in FeedbackOption]: boolean } = {
		missingInformation: false,
		notFactuallyCorrect: false,
		dontLikeTheTone: false,
		other: false,
	};
	if (previousFeedback) {
		Object.keys(previousFeedback).forEach(key => {
			if (key in initialFeedback) {
				initialFeedback[key as FeedbackOption] = previousFeedback[key as FeedbackOption];
			}
		});
	}
	return initialFeedback;
};

const feedbackSummaryMutation = graphql`
	mutation DislikeModalFeedbackSummaryMutation(
		$meetingRecordingSummaryId: ID!
		$feedback: [String!]
		$otherFeedback: String
		$source: MeetingRecordingPromptFeedbackSource!
	) {
		summaryFeedback(
			meetingRecordingSummaryId: $meetingRecordingSummaryId
			feedback: $feedback
			otherFeedback: $otherFeedback
			source: $source
		) {
			meetingRecordingSummary {
				id
				feedback {
					id
					missingInformation
					notFactuallyCorrect
					dontLikeTheTone
					other
					otherFeedback
				}
			}
		}
	}
`;

const dislikeModalFragment = graphql`
	fragment DislikeModal_meetingRecording on MeetingRecording {
		id
		canRegenerateSummary
		activeSummary {
			id
			feedback {
				id
				missingInformation
				notFactuallyCorrect
				dontLikeTheTone
				other
				otherFeedback
			}
		}
	}
`;

interface FeedbackOptionsProps {
	feedbackKey: FeedbackOption;
	onToggle(key: string): void;
	active: boolean;
}

function FeedbackOption({ feedbackKey, onToggle, active }: FeedbackOptionsProps) {
	const onClick = React.useCallback(() => {
		onToggle(feedbackKey);
	}, [feedbackKey, onToggle]);

	return (
		<Button
			size="slim"
			variant="secondary"
			onClick={onClick}
			className={cx(styles.feedbackOption, { [styles.active]: active })}
		>
			{feedbackOptionsText[feedbackKey]}
		</Button>
	);
}

interface DislikeModalProps {
	meetingRecording: DislikeModal_meetingRecording$key;
	source: HighlightFeedbackSource;
	onRegenerate(): void;
}

export default function DislikeModal({
	meetingRecording: meetingRecordingKey,
	source,
	onRegenerate,
	close,
}: ModalProps<{}> & DislikeModalProps) {
	const meetingRecording = useFragment(dislikeModalFragment, meetingRecordingKey);
	const [feedback, setFeedback] = React.useState<{ [key: string]: boolean }>(
		getInitialFeedbackState(meetingRecording.activeSummary?.feedback),
	);
	const [otherFeedback, setOtherFeedback] = React.useState<string>(
		meetingRecording.activeSummary?.feedback?.otherFeedback ?? "",
	);

	const onToggle = React.useCallback((key: string) => {
		setFeedback(prevFeedback => ({ ...prevFeedback, [key]: !prevFeedback[key] }));
	}, []);

	const feedbackSelected = React.useMemo(() => Object.keys(feedback).filter(key => feedback[key]), [feedback]);

	const sendFeedback = React.useCallback(async () => {
		if (!meetingRecording.activeSummary || !feedbackSelected) return;
		commitMutationPromise<DislikeModalFeedbackSummaryMutation>(environment, {
			mutation: feedbackSummaryMutation,
			variables: {
				meetingRecordingSummaryId: meetingRecording.activeSummary.id,
				feedback: feedbackSelected,
				otherFeedback,
				source,
			},
		});
	}, [meetingRecording.activeSummary, feedbackSelected, otherFeedback, source]);

	const onClose = React.useCallback(() => {
		sendFeedback();
		close({ feedback: feedbackSelected, otherFeedback: otherFeedback });
	}, [sendFeedback, close, feedbackSelected, otherFeedback]);

	const onRegenerateRecap = React.useCallback(() => {
		onRegenerate();
		onClose();
	}, [onRegenerate, onClose]);

	return (
		<Modal onClose={onClose}>
			<Modal.Header onClose={onClose} noBottomBorder className={styles.header}>
				Regenerate recap?
			</Modal.Header>
			<Modal.Body className={styles.content}>
				Thanks for your feedback.{" "}
				{meetingRecording.canRegenerateSummary &&
					`The Note Taker can generate a new recap for this meeting.
					Before doing so, tell us what you didn't like about this AI-generated content.`}
				<small>What was wrong with this feedback?</small>
				<div className={styles.feedbackOptions}>
					{Object.keys(feedback).map(key => (
						<FeedbackOption key={key} feedbackKey={key as FeedbackOption} onToggle={onToggle} active={feedback[key]} />
					))}
				</div>
				<div className={styles.otherFeedback}>
					<small>Can you tell us more?</small>
					<TextField value={otherFeedback} setValue={setOtherFeedback} placeholder="Share your feedback here" />
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="secondary" onClick={onClose}>
					Cancel
				</Button>
				{meetingRecording.canRegenerateSummary && (
					<Button size="normal" variant="primary" onClick={onRegenerateRecap} className={styles.regenerateButton}>
						<FontAwesomeIcon icon={faSync} />
						Regenerate
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
}
